import { entityRefToUrn } from '@agilelab/plugin-wb-builder-common';
import {
  HighlightFragment,
  highlightFragmentZod,
  SelectedSearchResponse,
} from '@agilelab/plugin-wb-marketplace-common';
import {
  AiIcon,
  useEnvironmentsContext,
  WbButton,
  WbSearch,
} from '@agilelab/plugin-wb-platform';
import {
  ALL_TAXONOMIES_FILTER,
  useTaxonomySelection,
} from '@agilelab/plugin-wb-practice-shaper';
import { MARKETPLACE_PROJECTS_DATA_LANDSCAPE } from '@agilelab/plugin-wb-search-common';
import { useApi } from '@mainstage/core-plugin-api';
import {
  Box,
  InputAdornment,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import {
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
} from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';
import SendIcon from '@material-ui/icons/Send';
import lodash from 'lodash';
import { useEffect, useId, useMemo, useState } from 'react';
import useDebounce from 'react-use/lib/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { SearchTaxonomySelector } from './SearchTaxonomySelector';
import clsx from 'clsx';
import { marketplaceApiRef } from '../../../plugin';

interface SearchPageAutocompleteProps {
  onChange: (value: string) => void;
  query: string;
  id: string;
  mode?: 'keyword' | 'semantic';
  semanticAvailable?: boolean;
  onModeChange?: (mode: 'keyword' | 'semantic') => void;
  handleSubmit: (event: any, value: string) => void;
}

const useStyles = makeStyles<Theme, { gradientId: string }>(
  theme => ({
    optionContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },
    highlight: {
      color: theme.palette.primary.main,
      fontWeight: 700,
    },
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5),
      },
    },
    taxonomySelectorWrapper: {
      width: 'auto',
      marginRight: '-1px',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
      },
      '& .MuiFormControl-root': {
        minHeight: 'auto',
        [theme.breakpoints.down('xs')]: {
          width: '100%',
        },
      },
      '& .MuiInputBase-root': {
        height: '52px !important',
        minHeight: '52px',
        borderRadius: '6px 0px 0px 6px !important',
        border: '1px solid rgba(0, 0, 0, 0.23) !important',
        backgroundColor: 'white',
        [theme.breakpoints.down('xs')]: {
          borderRadius: '6px !important',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        [theme.breakpoints.down('xs')]: {
          border: '1px solid rgba(0, 0, 0, 0.23) !important',
        },
      },
      '& fieldset': {
        // borderRight: 'none !important',
        [theme.breakpoints.down('xs')]: {
          border: '1px solid rgba(0, 0, 0, 0.23) !important',
        },
      },
      '&:hover': {
        zIndex: 1,
      },
    },
    searchBox: {
      width: '60%',
      boxShadow: theme.shadows[2],
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        borderRadius: '6px',
      },
    },
    searchBorderRadius: {
      borderTopRightRadius: '6px !important',
      borderBottomRightRadius: '6px !important',
      borderTopLeftRadius: '0px !important',
      borderBottomLeftRadius: '0px !important',
      [theme.breakpoints.down('xs')]: {
        borderRadius: '6px !important',
      },
    },
    searchInput: {
      height: '52px !important',
      borderLeft: 'none !important',
      '& input': { minWidth: 0 },
      [theme.breakpoints.down('xs')]: {
        height: '40px !important',
      },
    },
    modeSwitch: {
      flexShrink: 0,
      height: 28,
      padding: 2,
      borderRadius: 16,
      backgroundColor: theme.palette.grey[200],
      '& button[type="button"]': {
        minWidth: 78,
        padding: '2px 10px',
        textTransform: 'none',
        border: '1px solid transparent',
        borderRadius: 14,
        color: theme.palette.primary.main,
        fontSize: 12,
        lineHeight: '20px',
        margin: 0,
        '&.Mui-selected': {
          backgroundColor: theme.palette.white,
          color: theme.palette.primary.main,
          boxShadow: '0 2px 3px rgba(0, 0, 0, 0.16)',
          '&:hover': { backgroundColor: theme.palette.white },
        },
      },
      '& svg': { width: 16, height: 16, marginRight: theme.spacing(0.5) },
      '& button.Mui-selected[value="semantic"]': {
        color: theme.palette.accent.main,
        background: `linear-gradient(${theme.palette.white}, ${theme.palette.white}) padding-box, linear-gradient(90deg, #EE72D3, #7C4DFF) border-box`,
      },
      [theme.breakpoints.down('xs')]: {
        '& button[type="button"]': { minWidth: 62, padding: '2px 6px' },
      },
    },
    gradientIcon: {
      '& path': {
        fill: ({ gradientId }: { gradientId: string }) => `url(#${gradientId})`,
      },
    },
    gradientText: {
      background: 'linear-gradient(90deg, #EE72D3, #7C4DFF)',
      backgroundClip: 'text',
      color: 'transparent',
    },
    semanticSubmit: {
      border: '1px solid transparent',
      color: theme.palette.accent.main,
      background: `linear-gradient(${theme.palette.white}, ${theme.palette.white}) padding-box, linear-gradient(90deg, #EE72D3, #7C4DFF) border-box`,
      boxShadow: 'none',
      '&:hover': {
        background: `linear-gradient(${theme.palette.white}, ${theme.palette.white}) padding-box, linear-gradient(90deg, #EE72D3, #7C4DFF) border-box`,
        boxShadow: 'none',
      },
    },
  }),
  { name: 'WbSearchAutocomplete' },
);

export function getDocumentFieldValues(
  document: any,
  condition: (el: string, key: string) => boolean,
  key: string = '',
): string[] {
  if (Array.isArray(document)) {
    return document.flatMap((value: any) => {
      return getDocumentFieldValues(value, condition, key);
    });
  } else if (typeof document === 'object') {
    return lodash.toPairs(document).flatMap(([k, value]: [string, any]) => {
      return getDocumentFieldValues(
        value,
        condition,
        key !== '' ? `${key}.${k}` : k,
      );
    });
  }
  const stringValue = document.toString();
  if (condition(stringValue, key)) {
    return [stringValue];
  }
  return [];
}

export function finalizeTagString(
  valueWithTags: string,
  maxLength: number,
  startTag: string,
  endTag: string,
): string {
  const matchedValue = valueWithTags;
  const valueWithoutTags = valueWithTags
    .replaceAll(endTag, '')
    .replaceAll(startTag, '');

  // if the string is longer than the max length, we need to cut it
  if (valueWithoutTags.length > maxLength) {
    const preTagIndex = matchedValue.indexOf(startTag);
    let postTagIndex = matchedValue.lastIndexOf(endTag);
    let valueBetweenTags = matchedValue.slice(preTagIndex, postTagIndex);
    let valueBetweenTagsWithoutTags = valueBetweenTags
      .replaceAll(endTag, '')
      .replaceAll(startTag, '');

    // keep removing the last tag until the string is within the max length
    while (
      valueBetweenTagsWithoutTags.length > maxLength &&
      postTagIndex !== -1
    ) {
      const relativePostTagIndex = valueBetweenTags.lastIndexOf(endTag);
      if (relativePostTagIndex !== -1) {
        postTagIndex = preTagIndex + relativePostTagIndex;
        valueBetweenTags = matchedValue.slice(preTagIndex, postTagIndex);
        valueBetweenTagsWithoutTags = valueBetweenTags
          .replaceAll(endTag, '')
          .replaceAll(startTag, '');
      } else {
        postTagIndex = -1;
      }
    }

    // if the string is still longer than the max length, we cut it to the maximum possible value, without tags
    if (valueBetweenTagsWithoutTags.length > maxLength || postTagIndex === -1) {
      return valueBetweenTagsWithoutTags.slice(0, maxLength);
    }

    // from this point on, the first and last tag indexes are guaranteed to be within the max length
    // remove all leading and trailing tags
    const refined: string =
      matchedValue
        .slice(0, preTagIndex)
        .replaceAll(startTag, '')
        .replaceAll(endTag, '') +
      matchedValue.slice(preTagIndex, postTagIndex + endTag.length) +
      matchedValue
        .slice(postTagIndex + endTag.length)
        .replaceAll(startTag, '')
        .replaceAll(endTag, '');

    // calculate the remaining length: this is the number of additional characters we can return before and after the tags
    const remainingTotalLength = maxLength - valueBetweenTagsWithoutTags.length;

    // calculate the left index bound
    const calculateLeftBound = (
      input: string,
      remaining: number,
      startIndex: number,
    ) => {
      const leftIndexBound =
        startIndex - remaining < 0 ? 0 : startIndex - remaining;
      if (leftIndexBound === 0) {
        return 0;
      }
      const sliceIndex = input
        .slice(0, startIndex)
        .indexOf(' ', leftIndexBound);
      return sliceIndex !== -1 ? sliceIndex + 1 : startIndex;
    };
    const leftBound = calculateLeftBound(
      refined,
      remainingTotalLength,
      preTagIndex,
    );

    // calculate the right index bound
    const remainingRightLength =
      remainingTotalLength - (preTagIndex - leftBound);

    const calculateRightBound = (
      input: string,
      remaining: number,
      endIndex: number,
      endTagLength: number,
    ) => {
      const rightIndexBound =
        endIndex + endTagLength + remaining > input.length
          ? input.length
          : endIndex + endTagLength + remaining;
      if (rightIndexBound === input.length) {
        return input.length;
      }
      const sliceIndexRight = input.lastIndexOf(' ', rightIndexBound);
      return sliceIndexRight !== -1 ? sliceIndexRight : endIndex;
    };
    const rightBound = calculateRightBound(
      refined,
      remainingRightLength,
      postTagIndex,
      endTag.length,
    );

    // return the refined string sliced between the two boundaries
    return refined.slice(leftBound, rightBound);
  }

  return matchedValue;
}

export function getAutocompleteSuggestions(
  response?: Pick<SelectedSearchResponse, 'items'>,
): HighlightFragment[] {
  const suggestions = new Map<string, HighlightFragment>();
  for (const item of response?.items ?? []) {
    for (const highlight of item.highlights ?? []) {
      for (const fragment of highlight.fragments) {
        if (!suggestions.has(fragment.text)) {
          suggestions.set(fragment.text, fragment);
        }
      }
    }
  }
  return [...suggestions.values()];
}

export function getAutocompleteHighlightParts(
  fragment: HighlightFragment,
): { text: string; highlighted: boolean }[] {
  const parsed = highlightFragmentZod.safeParse(fragment);
  if (!parsed.success) return [{ text: fragment.text, highlighted: false }];
  const points = Array.from(fragment.text);
  const parts: { text: string; highlighted: boolean }[] = [];
  let position = 0;
  for (const range of parsed.data.ranges) {
    if (position < range.start) {
      parts.push({
        text: points.slice(position, range.start).join(''),
        highlighted: false,
      });
    }
    parts.push({
      text: points.slice(range.start, range.end).join(''),
      highlighted: true,
    });
    position = range.end;
  }
  if (position < points.length) {
    parts.push({ text: points.slice(position).join(''), highlighted: false });
  }
  return parts;
}

export const SearchPageAutocomplete = ({
  onChange,
  query,
  handleSubmit,
  id,
  mode = 'keyword',
  semanticAvailable = false,
  onModeChange,
}: SearchPageAutocompleteProps) => {
  const [open, setOpen] = useState(false);
  const { priorityEnvironment } = useEnvironmentsContext();
  const marketplaceApi = useApi(marketplaceApiRef);
  const gradientId = useId();
  const classes = useStyles({ gradientId });
  const [highlight, setHighlight] = useState<string>('');
  const { selectedTaxonomyRef, shouldDisable } = useTaxonomySelection();
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useDebounce(() => setDebouncedQuery(query.trim()), 500, [query]);
  const queryReady =
    mode === 'keyword' && !!query.trim() && debouncedQuery === query.trim();
  const { data: autocompleteValues } = useQuery({
    queryKey: [
      'marketplace-home-autocomplete',
      mode,
      priorityEnvironment.name,
      selectedTaxonomyRef,
      query.trim(),
      debouncedQuery,
    ],
    queryFn: ({ signal }) =>
      marketplaceApi.search(
        {
          mode: 'keyword',
          environment: priorityEnvironment.name,
          query: debouncedQuery,
          filters:
            selectedTaxonomyRef === ALL_TAXONOMIES_FILTER
              ? []
              : [
                  {
                    id: MARKETPLACE_PROJECTS_DATA_LANDSCAPE.field,
                    operator: 'in',
                    values: [entityRefToUrn(selectedTaxonomyRef)],
                  },
                ],
          category: 'selected',
          useHighlight: true,
          select: [],
          limit: 8,
        },
        { signal },
      ),
    enabled: queryReady,
    retry: false,
  });
  useEffect(() => {
    setHighlight('');
    if (mode !== 'keyword' || !query.trim()) {
      setOpen(false);
    }
  }, [query, mode, selectedTaxonomyRef, priorityEnvironment.name]);

  const suggestions = useMemo(
    () => (queryReady ? getAutocompleteSuggestions(autocompleteValues) : []),
    [autocompleteValues, queryReady],
  );
  const mappedAutocompleteValues = suggestions.map(fragment => fragment.text);

  const getAutocompleteString = (option: string) => {
    const fragment = suggestions.find(suggestion => suggestion.text === option);
    if (!fragment) return option;
    return getAutocompleteHighlightParts(fragment).map((part, index) =>
      part.highlighted ? (
        <span className={classes.highlight} key={index}>
          {part.text}
        </span>
      ) : (
        part.text
      ),
    );
  };

  const modeSelector = semanticAvailable ? (
    <InputAdornment position="start">
      <svg width="0" height="0" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#EE72D3" />
            <stop offset="100%" stopColor="#7C4DFF" />
          </linearGradient>
        </defs>
      </svg>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={mode}
        className={classes.modeSwitch}
        aria-label="Search mode"
        onChange={(_event, nextMode) => {
          if (nextMode && nextMode !== mode) {
            setOpen(false);
            setHighlight('');
            onModeChange?.(nextMode);
          }
        }}
      >
        <ToggleButton value="keyword" aria-label="Keyword search">
          <SearchIcon />
          Search
        </ToggleButton>
        <ToggleButton value="semantic" aria-label="Semantic search">
          <AiIcon
            className={mode === 'semantic' ? classes.gradientIcon : undefined}
          />
          <span
            className={mode === 'semantic' ? classes.gradientText : undefined}
          >
            Ask
          </span>
        </ToggleButton>
      </ToggleButtonGroup>
    </InputAdornment>
  ) : undefined;
  const submitButton = (
    <WbButton
      type="submit"
      aria-label={mode === 'semantic' ? 'Ask' : 'Search'}
      icon={
        mode === 'semantic' ? (
          <SendIcon className={classes.gradientIcon} />
        ) : (
          <SearchIcon />
        )
      }
      iconColor={mode === 'semantic' ? undefined : 'white'}
      className={mode === 'semantic' ? classes.semanticSubmit : undefined}
      disabled={mode === 'semantic' && !query.trim()}
    />
  );

  return (
    <Box className={classes.container}>
      {!shouldDisable && (
        <Box className={classes.taxonomySelectorWrapper}>
          <SearchTaxonomySelector />
        </Box>
      )}
      {mode === 'semantic' ? (
        <Box className={classes.searchBox}>
          <WbSearch
            id={id}
            label=""
            value={query}
            onChange={onChange}
            placeholder="How can I help you?"
            submitButton
            submitButtonContent={submitButton}
            InputProps={{ startAdornment: modeSelector }}
            className={classes.searchBorderRadius}
            inputClassName={clsx(
              classes.searchBorderRadius,
              classes.searchInput,
            )}
          />
        </Box>
      ) : (
        <Autocomplete
          id={id}
          className={classes.searchBox}
          options={[
            ...(query.trim() && !mappedAutocompleteValues.includes(query)
              ? [query]
              : []),
            ...mappedAutocompleteValues,
          ]}
          freeSolo
          disableClearable
          autoComplete
          onHighlightChange={(_event, newValue) => {
            if (newValue) setHighlight(newValue);
          }}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          noOptionsText="No Suggested Description"
          value={query || ''}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              setOpen(false);
              handleSubmit(
                event,
                highlight !== '' &&
                  highlight.length >=
                    (event.target as HTMLInputElement)?.value.length
                  ? highlight
                  : (event.target as HTMLInputElement)?.value,
              );
            }
          }}
          onChange={(_event, newValue, reason) => {
            if (typeof newValue === 'string') {
              onChange(newValue);
              if (reason === 'select-option') {
                handleSubmit(_event, newValue);
              }
            }
          }}
          filterOptions={x => x}
          getOptionLabel={option => {
            return typeof option === 'string' ? option : option;
          }}
          renderOption={option => (
            <Box
              className={classes.optionContainer}
              onClick={() => {
                if (option === query) {
                  handleSubmit(null, query);
                }
              }}
            >
              <Typography>{getAutocompleteString(option)}</Typography>
            </Box>
          )}
          renderInput={params => (
            <WbSearch
              label=""
              {...params}
              value={query}
              onChange={onChange}
              placeholder="Search by keyword..."
              // ensure a label from outside associated through id is provided
              provideAriaLabel={false}
              submitButton
              submitButtonContent={submitButton}
              InputProps={{
                ...params.InputProps,
                startAdornment: modeSelector,
              }}
              className={classes.searchBorderRadius}
              inputClassName={clsx(
                classes.searchBorderRadius,
                classes.searchInput,
              )}
              hasAutocomplete
            />
          )}
        />
      )}
    </Box>
  );
};
